import { IntlShape } from "react-intl"
import { ADD_ZERO, REMOVE_ZERO } from "constants/session"
import { ToPercentageProps } from "./numbers.interfaces"

interface Magnitude {
	baseLength: number
	referenceValue: number
	abbreviation: string
}

const IS_NUMBER_REGEX = /^-?[0-9]+$/
export const ZERO = 0
export const ONE = 1
export const CHILEAN_CURRENCY = "CLP"
export const USA_CURRENCY = "USD"
export enum SUPPORTED_CURRENCIES {
	CHILEAN_CURRENCY,
	USA_CURRENCY,
}
export const TWO_DECIMALS = 2

export enum DATA_STORAGE_MEASUREMENT_UNITS {
	B,
	KB,
	MB,
	GB,
	TB,
}

export const isNumber = (strNumber: string) => {
	return IS_NUMBER_REGEX.test(strNumber)
}

export const mod = (x: number, n: number) => {
	return ((x % n) + n) % n
}

export const arraySum = (numbers: Array<number>) => {
	return numbers.reduce((acc, curr) => {
		return acc + curr
	}, 0)
}

export const formatCurrency = (
	value: number = 0,
	currency: string,
	intl: IntlShape,
	decimals: number = 2
): string => {
	return `${currency} ${formatNumber(value, intl, decimals)}`
}

export const formatNumber = (
	value: number,
	intl: IntlShape,
	decimals: number = 2,
	spaceBetween: boolean = false
): string => {
	const { referenceValue, abbreviation } = resolveMagnitude(value)
	const abbreviatedNumber = transformToMagnitudeValue(
		value,
		referenceValue,
		decimals
	)
	const finalNumber = applyThousandsSeparator(abbreviatedNumber, intl)
	return spaceBetween
		? `${finalNumber} ${abbreviation}`
		: `${finalNumber}${abbreviation}`
}

/**
 * @deprecated The method should not be used
 */
export const formatPercentage = (
	value: number,
	decimals: number = 2,
	withPercentageChar = false,
	withPositiveSymbol = false
): string => {
	let abbreviatedNumber = transformToMagnitudeValue(
		value,
		1,
		decimals
	).toString()
	if (withPositiveSymbol) {
		abbreviatedNumber = `${getPositiveSymbol(value)}${abbreviatedNumber}`
	}
	return `${abbreviatedNumber}${withPercentageChar ? "%" : ""}`
}

export const toPercentage = (props: ToPercentageProps): string => {
	const {
		value,
		intl,
		decimals = 2,
		withPercentageChar = false,
		withPositiveSymbol = false,
	} = props
	const abbreviatedNumber = transformToMagnitudeValue(value, 1, decimals)
	let finalNumber = applyThousandsSeparator(abbreviatedNumber, intl)
	if (withPositiveSymbol) {
		finalNumber = `${getPositiveSymbol(value)}${finalNumber}`
	}
	return `${finalNumber}${withPercentageChar ? "%" : ""}`
}

export const getPositiveSymbol = (value: number) => {
	return value > 0 ? "+" : ""
}

export const transformToMagnitudeValue = (
	value: number,
	magnitudeReferenceValue: number,
	decimals: number
): string => {
	return (value / magnitudeReferenceValue).toFixed(decimals)
}

export const resolveMagnitude = (value: number): Magnitude => {
	const valueLength = Math.abs(value).toFixed(0).length
	const magnitudeItem = magnitudes.find((item) => valueLength > item.baseLength)
	return magnitudeItem ? magnitudeItem : defaultMagnitude
}

const defaultMagnitude: Magnitude = {
	baseLength: 0,
	referenceValue: 1,
	abbreviation: "",
}
const magnitudes: Array<Magnitude> = [
	{ baseLength: 18, referenceValue: 1000000000000000000, abbreviation: "T" },
	{ baseLength: 15, referenceValue: 1000000000000000, abbreviation: "MB" },
	{ baseLength: 12, referenceValue: 1000000000000, abbreviation: "B" },
	{ baseLength: 9, referenceValue: 1000000000, abbreviation: "MM" },
	{ baseLength: 6, referenceValue: 1000000, abbreviation: "M" },
	{ baseLength: 3, referenceValue: 1000, abbreviation: "K" },
	defaultMagnitude,
]

export const applyThousandsSeparator = (
	number: string,
	intl: IntlShape
): string => {
	return intl.formatNumber(Number(number))
}

export const romanNumeralConverter = (number: string | number) => {
	const roman: Record<string, number> = {
		M: 1000,
		CM: 900,
		D: 500,
		CD: 400,
		C: 100,
		XC: 90,
		L: 50,
		XL: 40,
		X: 10,
		IX: 9,
		V: 5,
		IV: 4,
		I: 1,
	}
	let str = ""
	let tempNumber = Number(number)

	for (const i of Object.keys(roman)) {
		const q = Math.floor(tempNumber / roman[i])
		tempNumber -= q * roman[i]
		str += i.repeat(q)
	}

	return str
}

const getGreatestCommonDivisor = (a: number, b: number) => {
	if (b > a) {
		const temp = a
		a = b
		b = temp
	}
	while (b !== 0) {
		const module = a % b
		a = b
		b = module
	}
	return a
}

export const getAssetRatio = (width: number, height: number) => {
	const divisor =
		width >= height ? height : getGreatestCommonDivisor(width, height)
	const numerator = fixIntOrFloat(width / divisor)
	return `${numerator}:${height / divisor}`
}

const fixIntOrFloat = (value: number) =>
	Number.isSafeInteger(value) ? value : value.toFixed(2)

export const addZeroToTimer = (n: number) => {
	return (n < 10 ? ADD_ZERO : REMOVE_ZERO) + n
}

export const convertToBytes = (
	amount: number,
	unit: DATA_STORAGE_MEASUREMENT_UNITS
): number => {
	if (unit === DATA_STORAGE_MEASUREMENT_UNITS.B) {
		return amount
	}
	return convertToBytes(amount * 1024, unit - 1)
}
